import { ApiError } from '@ddt/api-client';
import type { FieldError } from './types';

/** Erro da Balance Admin API (problem+json: `title` leva o código estável). */
export class BalanceApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    readonly detail: string,
    readonly errors: FieldError[] = [],
  ) {
    super(detail || code);
    this.name = 'BalanceApiError';
  }
}

interface ProblemBody {
  title?: string;
  detail?: string;
  errors?: FieldError[];
}

export function toBalanceError(status: number, body: unknown): BalanceApiError {
  const problem = (typeof body === 'object' && body !== null ? body : {}) as ProblemBody;
  return new BalanceApiError(status, problem.title ?? 'error', problem.detail ?? '', Array.isArray(problem.errors) ? problem.errors : []);
}

/** Resultado do openapi-fetch → dado, ou lança `BalanceApiError`. */
export function unwrap<T>(result: { data?: T; error?: unknown; response: Response }): T {
  if (!result.response.ok) throw toBalanceError(result.response.status, result.error);
  return result.data as T;
}

/** Para respostas sem corpo (204). */
export function ensureOk(result: { error?: unknown; response: Response }): void {
  if (!result.response.ok) throw toBalanceError(result.response.status, result.error);
}

const TEXT: Record<string, string> = {
  invalid_credentials: 'Usuário ou senha incorretos.',
  account_locked: 'Muitas tentativas. Aguarde alguns minutos e tente de novo.',
  account_banned: 'Esta conta está banida.',
  rate_limited: 'Muitas requisições em pouco tempo. Aguarde um instante.',
  not_admin: 'Esta conta não tem perfil de administrador.',
  unauthorized: 'Sua sessão não é válida. Entre de novo.',
  invalid_token: 'Sua sessão expirou. Entre de novo.',
  insufficient_role: 'Apenas administradores podem usar este painel.',
  validation_error: 'Há valores inválidos. Confira os campos destacados.',
  not_draft: 'Só rascunhos podem ser alterados. Crie um novo rascunho a partir desta versão.',
  already_published: 'Esta versão já está em vigor.',
  version_conflict: 'Outra pessoa criou uma versão ao mesmo tempo. Tente de novo.',
  not_found: 'Não encontrado.',
};

export class NotAdminError extends Error {
  readonly code = 'not_admin';
  constructor() {
    super(TEXT.not_admin);
  }
}

/** Texto para o usuário a partir de qualquer erro. */
export function describeError(error: unknown): string {
  if (error instanceof NotAdminError) return error.message;
  if (error instanceof BalanceApiError || error instanceof ApiError) {
    if (error.status === 429) return TEXT.rate_limited!;
    if (error.status >= 500) return 'O servidor teve um problema. Tente de novo em instantes.';
    return TEXT[error.code] ?? (error.detail || 'Não foi possível concluir a ação.');
  }
  if (error instanceof TypeError) return 'Sem conexão com o servidor.';
  if (error instanceof Error && error.message) return error.message;
  return 'Algo deu errado.';
}

/** Erros por campo vindos da API (`parameters.guardDivisor` → mensagem). */
export function serverFieldErrors(error: unknown): Record<string, string> {
  if (!(error instanceof BalanceApiError)) return {};
  return Object.fromEntries(error.errors.map((e) => [e.field, e.message]));
}
